/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useLoggedInState } from '~/composables/state/useLoggedInState'
import { useTabBroadcastChannel } from '~/composables/useTabBroadcastChannel'
import { setUserState } from '~/composables/state/useUserState'
import type { User } from '~/types/auth.model'
import type { Event, Message } from '~/composables/useTabBroadcastChannel'

export async function login(): Promise<void> {
  const route = useRoute()
  const { redirect_url } = await $fetch<{ redirect_url: string }>(`/api/auth/login?next=${encodeURIComponent(route.fullPath)}`)
  await navigateTo({ path: redirect_url }, { external: true })
}

export async function logout(): Promise<void> {
  await $fetch('/api/auth/logout')
  const isLoggedIn = useLoggedInState()
  isLoggedIn.value = false
}

export async function fetchUserInfo(): Promise<User> {
  // Send cookie to SSR with each request
  const { authCookieName } = useAppConfig()
  const sessionId = useCookie(authCookieName).value

  return await $fetch<User>('/api/users/info', {
    headers: {
      Cookie: `${authCookieName}=${String(sessionId)}`,
    },
    credentials: 'include',
  })
}

const APP_STORE_BROADCAST_NAME = 'app-store-broadcast-channel'

/**
 * `useSyncAuth` should only be called once in the parent component highest in the hierarchy.
 */
export const useSyncAuth = () => {
  const { authCookieName } = useAppConfig()
  const sessionId = useCookie(authCookieName)
  const isLoggedIn = useLoggedInState(() => !!sessionId.value)
  const broadcastChannel = useTabBroadcastChannel(APP_STORE_BROADCAST_NAME)

  const handleBroadcastMessage = (event: Event<Message>) => {
    switch (event.data.eventName) {
      case 'refreshLogin':
        refreshCookie(authCookieName)
        sessionId.value = useCookie(authCookieName).value
        isLoggedIn.value = !!sessionId.value
        if (!isLoggedIn.value) {
          setUserState(null)
        }
        break
      case 'updateUser':
        setUserState(event.data.user)
        break
      default:
        break
    }
  }

  onMounted(async () => {
    if (isLoggedIn.value) {
      const user = await fetchUserInfo()
      setUserState(user)
      broadcastChannel.postMessage({ eventName: 'updateUser', user })
    }

    broadcastChannel.postMessage({ eventName: 'refreshLogin' })
    broadcastChannel.onmessage = handleBroadcastMessage
  })

  watch(isLoggedIn, (newLoggedInState) => {
    if (newLoggedInState) {
      return
    }

    // Broadcast logout state to other browser tabs
    sessionId.value = null
    setUserState(null)
    broadcastChannel.postMessage({ eventName: 'refreshLogin' })
    broadcastChannel.postMessage({ eventName: 'updateUser', user: null })
  })
}
